import { h } from 'preact';
import { useMemo, useState, useEffect } from 'preact/hooks';
import Image from 'utils/image';
import ProductQuantity from 'utils/product-quantity';
import { formatMoney } from 'utils/storefront/format-resource';
import { withCartContext } from './cart-context';
import startCountdown from '../utils/startCountDown';

const CountDownTimer = ({ flashEnd, nowDate }) => (
  <div className="button pointer-events-none relative mb-1 mt-6 !h-auto min-h-33 w-full rounded-lg border-gray-4 !py-12">
    <div
      className='relative flex w-auto
          pl-34 pr-12 font-belltownBold text-16 text-default 
          before:absolute before:left-10 before:top-1/2 before:block before:h-18 before:w-18 
          before:-translate-y-1/2 before:bg-[image:var(--clock)] before:bg-center before:bg-no-repeat before:content-[""] hover:bg-default'
      data-flashsale-end-date={flashEnd}
      data-now={nowDate}
    >
      {flashEnd < nowDate ? 'Ended' : ''}
    </div>
  </div>
);

const CartItem = ({ cart, line, onCartError, productData = null }) => {
  const [qty, setQty] = useState(line.quantity);
  const [error, setError] = useState(false);
  let maxQuantity =
    line.merchandise?.quantityAvailable || (productData !== null ? productData.quantityAvailable : Infinity);
  const nowDate = Math.floor(Date.now() / 1000);

  const handleCartUpdate = () => {
    setError(false);
  };

  useEffect(() => {
    const initCountdownClock = () => {
      const endDateTimestamp = parseInt(line.properties._flashEnd, 10) * 1000;
      const nowTimestamp = nowDate * 1000;
      const el = document.querySelector(
        `[data-line="${line.id}"] [data-flashsale-end-date="${line.properties._flashEnd}"]`,
      );
      if (el) {
        startCountdown(endDateTimestamp, nowTimestamp, el);
      }
    };
    initCountdownClock();
    document.addEventListener('cart:updated', handleCartUpdate);
    return () => {
      document.removeEventListener('cart:updated', handleCartUpdate);
    };
  }, []);

  if (line.children) {
    // If bundle, get lowest child quantity
    maxQuantity = line.children.reduce((prevQuantity, child) => {
      const childMax =
        (cart.mode === 'storefront' ? child.merchandise?.quantityAvailable : child.quantity_rule?.maxQuantity) ||
        Infinity;
      return childMax < prevQuantity ? childMax : prevQuantity;
    }, Infinity);
  }

  const handleAdjustQuantity = (quantity, silent = false) => {
    const itemQty = quantity > maxQuantity ? maxQuantity : quantity;
    let payload = [
      {
        id: line.id,
        quantity: itemQty,
      },
    ];

    // If bundle, add children to update request
    if (line.children) {
      payload = line.children.map((child) => ({
        id: child.id,
        attributes: child.attributes,
        quantity: qty,
      }));
    }

    return cart.update(payload, silent);
  };

  const handleRemove = (silent = false) => {
    let payload = [line.id];

    // If bundle, add children to update request
    if (line.children) {
      payload = line.children.map((child) => child.id);
    }

    return cart.remove(payload, silent);
  };

  const lineItemImage = line.children ? line.merchandise.product?.image?.src : line.image;
  const countDownTimer = useMemo(() => (
    <CountDownTimer
      flashEnd={line.properties._flashEnd}
      nowDate={nowDate}
    />
  ));

  useEffect(() => {
    if (line.quantity > maxQuantity) {
      if (maxQuantity > 0) {
        setError(true);
        handleAdjustQuantity(maxQuantity, true);
      } else {
        onCartError(window.TMW.Strings.cart.unavailable.replace('{{ product }}', line.product_title));
        handleRemove(true);
      }
    }
  }, [productData, line.quantity]);

  const lineItem = useMemo(
    () => (
      <div
        key={line.id}
        data-line={line.id}
      >
        <div className="flex items-center justify-center gap-16">
          <div className="flex min-h-[155px] min-w-[129px] items-center justify-center overflow-hidden rounded-2xl bg-gray-1">
            {lineItemImage && (
              <Image
                src={lineItemImage}
                classes="block object-contain w-[129px] h-auto mix-blend-multiply"
                alt={`${line.title} image`}
                width="130"
                height="130"
                srcWidths="200"
              />
            )}
          </div>
          <div className="flex min-h-157 flex-1 flex-col gap-8">
            <a
              className="font-belltownBold leading-130 tracking-xs no-underline"
              href={cart.mode === 'storefront' ? `/products/${line.merchandise.product.handle}` : line.url}
            >
              {cart.mode === 'storefront' ? line.merchandise?.product.title : line.product_title}
            </a>
            <div className="-mb-3 flex flex-wrap items-center gap-4">
              <div className="price h4 m-0 text-zulily-purple">
                {formatMoney({
                  amount: line.line_price / 100,
                  currencyCode: window.TMW?.localization?.currencyCode,
                })}
              </div>
            </div>
            <ul className="list-none p-0">
              {line.options_with_values.map((option) => (
                <li
                  className="m-0 text-12 tracking-xs"
                  key={option.value}
                >
                  <span className="inline-block pr-4">{option.name}:</span>
                  {option.value}
                </li>
              ))}
            </ul>
            <div className="flex items-center justify-between">
              <ProductQuantity
                quantity={line.quantity}
                changeQuantity={(value) => {
                  setQty(value);
                  handleAdjustQuantity(value);
                }}
                inventoryQuantity={maxQuantity}
                isRemovable={true}
              />
              <button
                className="text-12 tracking-xs underline"
                onClick={() => handleRemove()}
              >
                {window.TMW.Strings.cart.remove}
              </button>
            </div>
            {error && (
              <div className="body-2 text-error">
                {window.TMW.Strings.cart.lowInventory.replace('{{ quantity }}', productData?.quantityAvailable)}
              </div>
            )}
          </div>
        </div>
        {line.properties._flashStart <= nowDate && nowDate <= line.properties._flashEnd && countDownTimer}
      </div>
    ),
    [line, qty, productData, error],
  );

  return lineItem;
};

export default withCartContext(CartItem);
